import {useState, useCallback, SetStateAction, useEffect} from 'react';
import {
  Drawer,
  TextField,
  Stack,
  Typography,
  Divider,
  Box,
  Grid,
  Tab,
  Tabs,
  Alert,
  Snackbar
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useForm, FormProvider, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import DialogToolBar from './AdditionData/DialogToolBar';
import {CustomFile, UploadProps} from 'src/minimal-components/upload';
import {RHFCheckbox, RHFSingleUpload} from 'src/minimal-components/hook-form';
import Resources from './AdditionData/Resources';
import NewFilesLockProperties from './AdditionData/NewFilesLockProperties';
import {fileDatas, fileNameByUrl} from 'src/minimal-components/file-thumbnail';
import EncryptUtils from 'src/utils/EncryptUtils';
import uuidv4 from 'src/utils/uuidv4';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {enqueueSnackbar} from 'notistack';
import {useAppContext} from '@context/AppContext';
import {LoadingButton} from '@mui/lab';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import {BASE_RECORD_ID, LMS_IS_ADMIN} from 'src/routes/BaseRoute';
import {useModulePreviewContext} from '@context/ModulePreviewContextProvider';

const StyledLabel = styled('span')(({theme}) => ({
  ...theme.typography.caption,
  width: 100,
  flexShrink: 0,
  color: theme.palette.text.secondary,
  fontWeight: theme.typography.fontWeightSemiBold
}));

// Validation schema using yup
const validationSchema = yup.object().shape({
  taskName: yup.string().required('Task name is required'),
  duration: yup.number().nullable().notRequired(),
  taskDescription: yup.string().notRequired(),
  uploadResourcesfiles: yup
    .array()
    .of(
      yup.object().shape({
        file: yup.mixed().required('File is required'),
        id: yup.string().required()
      })
    )
    .min(1, 'At least one file must be uploaded')
    .max(1, 'Max file must be uploaded'),
  bloBurls: yup.array().of(
    yup.object().shape({
      id: yup.string(),
      name: yup.string(),
      size: yup.number(),
      type: yup.string(),
      fileExtension: yup.string(),
      blobUrl: yup.string()
    })
  ),
  displayprogress: yup.array().of(
    yup
      .object()
      .shape({
        display: yup.boolean(),
        id: yup
          .mixed()
          .test(
            'is-string-or-number',
            'ID must be a string or a number',
            value => typeof value === 'string' || typeof value === 'number'
          ),
        progress: yup.number()
      })
      .nullable()
      .notRequired()
  ),
  MarkFileAsMandatory: yup.boolean().nullable().notRequired(),
  MarkAsIntroductoryFile: yup.boolean().nullable().notRequired(),
  DisableSeek: yup.boolean().nullable().notRequired(),
  EnableFileDownload: yup.boolean().nullable().notRequired()
});
type UploadFile = {
  file: File;
  preview?: string;
  id: string;
};
type Bloburls = {
  id: string;
  name: string;
  type: string;
  size: number;
  blobUrl?: string;
};
type Displayprogress = {
  id?: string | number;
  display?: boolean;
  progress?: 100;
};
interface IKanbanTask {
  id: string;
  name: string;
  duration?: number | null;
  description?: string | null;
  uploadResourcesfiles?: UploadFile[];
  bloBurls?: Bloburls[];
  displayprogress?: Displayprogress[];
  MarkFileAsMandatory?: boolean;
  MarkAsIntroductoryFile?: boolean;
  DisableSeek?: boolean;
  EnableFileDownload?: boolean;
}
interface ModuleData {
  id: string;
  name: string;
  expanded: boolean;
  description?: string;
  duration?: string;
  typeId: number;
  type: string;
  sequence: number;
  module_lock_type_id?: number;
  locked_until_date?: string;
  Module_Completed?: boolean;
  IsUnlocked?: boolean;
  is_completed?: boolean;
  locked_linked_items?: {
    id: string;
    item_name: string;
    entity_completion_type_id: number;
    entity_completion_sub_type_id: number;
    module_link_id: number;
    module_id: number;
    type: 'file' | 'link' | 'content' | 'assignment' | 'onlinetest' | 'module';
  }[];
  files?: {
    id: string;
    name: string;
    ParentName: string;
    blob_file_extension?: string;
    typeId: number;
    fileexpanded: boolean;
    time?: string;
    score?: string;
    lock_type_id: number;
    locked_until_date?: string;
    type: string;
    module_id: number;
    sequence: number;
    ChildItems_Completed?: boolean;
    IsUnlocked?: boolean;
    is_completed?: boolean;
    additionalFiles?: {id: string; name: string}[];
    locked_linked_items?: {
      id: string;
      item_name: string;
      entity_completion_sub_type_id: number;
      entity_completion_type_id: number;
      module_link_id: number;
      module_id: number;
      type:
        | 'file'
        | 'link'
        | 'content'
        | 'assignment'
        | 'onlinetest'
        | 'module';
    }[];
  }[];
}
interface Propsnew extends Omit<UploadProps, 'file'> {
  multiple?: boolean;
}

interface Props extends Propsnew {
  task: any;
  uiFieldsdata: any;
  // eslint-disable-next-line react/boolean-prop-naming
  openDetails: boolean;
  taskDone: any;
  onCloseDetails: VoidFunction;
  onUpdateTask?: (updateTask: any) => void;
}

export default function AddFileSModule({
  task,
  uiFieldsdata,
  openDetails,
  onCloseDetails,
  taskDone,
  onUpdateTask,
  ...other
}: Props) {
  const {
    appState: {applicationName}
  } = useAppContext();
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );
  const lmsisAdmin = EncryptUtils.getDecryptedData(
    location?.search,
    LMS_IS_ADMIN
  );
  const {ModulePreviewData, SetModulePreviewData} = useModulePreviewContext();
  const [isloading, setIsloading] = useState(false);
  const [tabActive, setTabsActive] = useState<boolean>(true);
  const [uiFields, setUiFields] = useState<any>(uiFieldsdata);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [saved, setSaved] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isloaded, setIsLoaded] = useState(false);
  const [insertedId, setInsertedId] = useState<any>(undefined);

  const handleChange = (event: any, newValue: SetStateAction<number>) => {
    if (tabActive) {
      setOpenSnackbar(true); // Show the snackbar when `tabActive` is true
    } else {
      setActiveTab(newValue); // Switch tabs
    }
  };
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
    setSaved(false);
  };
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      taskName: task?.name || '',
      duration: task?.duration || null,
      taskDescription: task?.description || '',
      uploadResourcesfiles: task?.uploadResourcesfiles || [],
      bloBurls: task?.bloBurls || [], // Default to existing attachments
      MarkFileAsMandatory: task?.MarkFileAsMandatory || true,
      MarkAsIntroductoryFile: task?.MarkAsIntroductoryFile || false,
      DisableSeek: task?.DisableSeek || false,
      EnableFileDownload: task?.EnableFileDownload || false
    }
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {errors, isSubmitting}
  } = methods;

  const values = watch();
  const findFileInModule = (
    data: any[],
    moduleId: number,
    fileId: number,
    type: string
  ) => {
    const module = data.find(m => m.id == moduleId);
    if (!module) return null;
    const file = module.files?.find(
      (f: {id: number; type: string}) => f.id == fileId && f.type === type
    );
    return file ?? null;
  };
  const init = async () => {
    try {
      setIsloading(true);
      let res;
      if (uiFieldsdata === undefined || uiFieldsdata === undefined) {
        res = await APIService.getSearchForm(
          applicationName,
          'module',
          'AddFiles'
        );
        setUiFields(res.searchForm);
      } else {
        setUiFields(uiFieldsdata);
      }
      const fileData = findFileInModule(
        ModulePreviewData,
        Number(insertedId?.data[0].module_id),
        Number(insertedId?.data[0].inserted_id),
        insertedId?.data[0].type
      );
      if (fileData) {
        const updatedEntities = [
          {
            id: fileData.id ?? undefined, // Assign entity_id
            name: fileData.blob_name ?? '', // Assign sub_type_id
            type: fileData.blob_type ?? '',
            blobUrl: fileData.azure_blob_url ?? '',
            size: fileData.blob_size ?? 0,
            fileExtension: fileData.blob_file_extension ?? ''
          }
        ];
        const uploadfiles = [
          {
            id: fileData.id ?? '', // Fallback to empty string if undefined
            file: {
              id: fileData.id ?? '', // Ensure it has an ID
              name: fileData.blob_name ?? '', // Name of the file
              type: fileData.blob_type ?? '', // File type
              size: fileData.blob_size ?? 0, // File size in bytes
              lastModified: new Date().getTime(), // Mock last modified
              preview: fileData.azure_blob_url ?? '', // Use blob URL as a preview
              path: fileData.azure_blob_url ?? '', // Default path to undefined
              slice: () => new Blob().slice(), // Mock implementation
              arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)), // Mock implementation
              stream: () => new Blob().stream(), // Mock implementation
              webkitRelativePath: '' // Default empty string
            } as CustomFile,
            preview: fileData.azure_blob_url ?? '' // Set optional preview
          }
        ];
        const displayprogressdata = [
          {
            id: fileData.id ?? '',
            display: false,
            progress: 100
          }
        ];
        setValue('taskName', fileData.name || '');
        await setValue('bloBurls', updatedEntities, {shouldValidate: true});
        await setValue('uploadResourcesfiles', uploadfiles);
        await setValue('displayprogress', displayprogressdata);
        setValue('DisableSeek', fileData?.disable_seek);
        setValue('EnableFileDownload', fileData?.enable_file_download);
        setValue('MarkAsIntroductoryFile', fileData?.mark_as_introductory_file);
        setValue('duration', fileData?.time);
        setValue('taskDescription', fileData?.description);
      } else {
        const updatedEntitiestask = task?.data
          ? [
              {
                id: task?.data?.id ?? undefined, // Assign entity_id
                name: task?.data?.blob_name ?? '', // Assign sub_type_id
                type: task?.data?.blob_type ?? '',
                blobUrl: task?.data?.azure_blob_url ?? '',
                size: task?.data?.blob_size ?? 0,
                fileExtension: task?.data?.blob_file_extension ?? ''
              }
            ]
          : [];
        const uploadfilestask = task?.data
          ? [
              {
                id: task.data?.id ?? '', // Fallback to empty string if undefined
                file: {
                  id: task.data?.id ?? '', // Ensure it has an ID
                  name: task.data?.blob_name ?? '', // Name of the file
                  type: task.data?.blob_type ?? '', // File type
                  size: task.data?.blob_size ?? 0, // File size in bytes
                  lastModified: new Date().getTime(), // Mock last modified
                  preview: task.data?.azure_blob_url ?? '', // Use blob URL as a preview
                  path: task.data?.azure_blob_url ?? '', // Default path to undefined
                  slice: () => new Blob().slice(), // Mock implementation
                  arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)), // Mock implementation
                  stream: () => new Blob().stream(), // Mock implementation
                  webkitRelativePath: '' // Default empty string
                } as CustomFile,
                preview: task.data?.azure_blob_url ?? '' // Set optional preview
              }
            ]
          : [];
        const displayprogressdatatask = task?.data
          ? [
              {
                id: task.data?.id ?? '',
                display: false,
                progress: 100
              }
            ]
          : [];
        setValue('taskName', task?.data?.name || '');
        await setValue('bloBurls', updatedEntitiestask, {shouldValidate: true});
        setValue('uploadResourcesfiles', uploadfilestask);
        setValue('displayprogress', displayprogressdatatask);
        setValue('DisableSeek', task?.data?.disable_seek);
        setValue('EnableFileDownload', task?.data?.enable_file_download);
        setValue(
          'MarkAsIntroductoryFile',
          task?.data?.mark_as_introductory_file
        );
        setValue('duration', task?.data?.time);
        setValue('taskDescription', task?.data?.description);
        // if (
        //   (res?.searchForm || uiFieldsdata !== undefined) &&
        //   (insertedId || task?.inserted_id)
        // ) {
        //   const {sql_data} =
        //     uiFieldsdata['create_sql'] || res.searchForm['create_sql'];
        //   let operations;
        //   if (insertedId || task?.inserted_id) {
        //     operations = 'SEARCH';
        //   }
        //   const data: any = {
        //     sql_data: sql_data,
        //     data: {
        //       id: insertedId?.data[0]?.inserted_id || task?.inserted_id,
        //       course_overview_id: uniqueId,
        //       tableName: 'ModuleFileSubList'
        //     },
        //     operation: operations
        //   };
        //   const encryptRequestData = EncryptUtils.encryptedData(data);
        //   const result = await APIService.getLMSResultsData(
        //     applicationName,
        //     'LMS',
        //     {
        //       tk: encryptRequestData
        //     }
        //   );
        //   if (result.data && result.data.length > 0) {
        //     const updatedEntities = result.data.map(
        //       (resourceFile: {
        //         id: any;
        //         blob_name: string;
        //         blob_type: string;
        //         azure_blob_url: string;
        //         blob_size: number;
        //         blob_file_extension: string;
        //       }) => {
        //         return {
        //           id: resourceFile.id ?? undefined, // Assign entity_id
        //           name: resourceFile.blob_name ?? '', // Assign sub_type_id
        //           type: resourceFile.blob_type ?? '',
        //           blobUrl: resourceFile.azure_blob_url ?? '',
        //           size: resourceFile.blob_size ?? 0,
        //           fileExtension: resourceFile.blob_file_extension ?? ''
        //         };
        //       }
        //     );
        //     const uploadfiles = result.data.map(
        //       (resourceFile: {
        //         id: any;
        //         blob_name: string;
        //         blob_type: string;
        //         azure_blob_url: string;
        //         blob_size: number;
        //         blob_file_extension: string;
        //       }): UploadFile => ({
        //         id: resourceFile.id ?? '', // Fallback to empty string if undefined
        //         file: {
        //           id: resourceFile.id ?? '', // Ensure it has an ID
        //           name: resourceFile.blob_name ?? '', // Name of the file
        //           type: resourceFile.blob_type ?? '', // File type
        //           size: resourceFile.blob_size ?? 0, // File size in bytes
        //           lastModified: new Date().getTime(), // Mock last modified
        //           preview: resourceFile.azure_blob_url ?? '', // Use blob URL as a preview
        //           path: resourceFile.azure_blob_url ?? '', // Default path to undefined
        //           slice: () => new Blob().slice(), // Mock implementation
        //           arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)), // Mock implementation
        //           stream: () => new Blob().stream(), // Mock implementation
        //           webkitRelativePath: '' // Default empty string
        //         } as CustomFile,
        //         preview: resourceFile.azure_blob_url ?? '' // Set optional preview
        //       })
        //     );
        //     const displayprogressdata = result.data.map((res: {id: any}) => ({
        //       id: res.id ?? '',
        //       display: false,
        //       progress: 100
        //     }));
        //     // Update the form's entities field
        //     await setValue('bloBurls', updatedEntities, {shouldValidate: true});
        //     await setValue('uploadResourcesfiles', uploadfiles);
        //     await setValue('displayprogress', displayprogressdata);
        //     setValue('DisableSeek', result.data[0]?.disable_seek);
        //     setValue(
        //       'EnableFileDownload',
        //       result.data[0]?.enable_file_download
        //     );
        //     setValue(
        //       'MarkAsIntroductoryFile',
        //       result.data[0]?.mark_as_introductory_file
        //     );
        //     setValue('duration', result.data[0]?.time);
        //     setValue('taskDescription', result.data[0]?.description);
        //     setValue('taskName', result.data[0]?.name);
        //   }
        //   setIsloading(false);
        // }
      }
      setIsloading(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      setIsloading(false);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (task) {
        setTabsActive(false);
      }
      if (taskDone?.operation === 'INSERT' && insertedId === undefined) {
        setInsertedId(undefined);
      } else if (insertedId !== undefined) {
        setInsertedId(insertedId);
      } else {
        setInsertedId({
          data: [
            {
              module_id: task?.module_id,
              inserted_id: task?.inserted_id,
              type: 'file'
            }
          ]
        });
      }
      await init();
    };

    fetchData();
  }, [activeTab]);
  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const existingFiles = values.uploadResourcesfiles || [];
      const newFiles = await Promise.all(
        acceptedFiles.map(async file => {
          const uniqueID = uuidv4();
          return {
            id: uniqueID,
            file,
            preview: ''
          };
        })
      );

      // Merge existing files with newly dropped files
      setValue('uploadResourcesfiles', [...existingFiles, ...newFiles], {
        shouldValidate: true
      });
    },
    [setValue, values.uploadResourcesfiles]
  );
  const RemoveFileFromServer = async (file: string | CustomFile) => {
    try {
      if (values.bloBurls) {
        const {blobUrl, name} = values.bloBurls[0];

        const formData = new FormData();
        if (name && blobUrl) {
          const reqPayload = EncryptUtils.encryptedData({
            userId: fileNameByUrl(blobUrl)
          });
          formData.append('file', name);
          formData.append('tk', reqPayload);
        }
        // const encryptRequestData = EncryptUtils.encryptedData(formData);
        await APIService.postUploadData(
          'LMS',
          'module',
          formData,
          'removeFile'
        );
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };
  const handleRemoveFile = useCallback(
    async (inputFile: string | CustomFile) => {
      const {id} = fileDatas(inputFile) || {id: ''};
      const filtered = values.uploadResourcesfiles?.filter(fileObj => {
        return fileObj?.id !== id; // Access `id` from `file`
      });

      const blobupdate = values.bloBurls?.filter(fileObj => {
        return fileObj?.id !== id; // Compare `file` property
      });
      const displayUpdate = values.displayprogress?.filter(fileObj => {
        return fileObj?.id !== id;
      });
      await setValue('uploadResourcesfiles', filtered, {shouldValidate: true});
      await setValue('bloBurls', blobupdate, {shouldValidate: true});
      await setValue('displayprogress', displayUpdate);
      // await RemoveFileFromServer(inputFile);
    },
    [setValue, values.uploadResourcesfiles]
  );

  // Handle removing all files
  const handleRemoveAllFiles = useCallback(() => {
    setValue('uploadResourcesfiles', [], {shouldValidate: true});
  }, [setValue]);

  const onSubmit = async (datavalue: any) => {
    const attachments = datavalue?.uploadResourcesfiles.map(
      (fileObj: UploadFile) => ({
        name: fileObj.file.name,
        url: fileObj.preview
      })
    );
    onUpdateTask?.({
      ...task,
      name: datavalue.taskName,
      duration: datavalue?.duration,
      description: datavalue?.taskDescription,
      uploadResourcesfiles: attachments
    });
    try {
      if (uiFields) {
        const {sql_data} = uiFields['create_sql'];
        let operations;
        if (taskDone?.id && taskDone?.operation) {
          operations = taskDone?.operation;
          datavalue.module_id = taskDone?.id;
        }
        if (insertedId && insertedId.data.length > 0) {
          operations = 'UPDATE';
          datavalue.id = insertedId.data[0].inserted_id;
        }
        datavalue.isAdmin = lmsisAdmin === 'True';
        datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
        datavalue.blobname = datavalue.bloBurls[0].name;
        datavalue.blobtype = datavalue.bloBurls[0].type;
        datavalue.blobsize = datavalue.bloBurls[0].size;
        datavalue.blobfileExtension = datavalue.bloBurls[0].fileExtension;
        datavalue.course_overview_id = uniqueId;
        datavalue.page = 1;
        setIsLoaded(true);
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await APIService.getLMSResultsData(
          applicationName,
          'LMS',
          {
            tk: encryptRequestData
          }
        );
        if (result.data[0]) {
          setInsertedId({data: [{...result.data[0], type: 'file'}]});
        }
        const processModulesData = (data: ModuleData[]) => {
          return data.map(module => ({
            ...module,
            files:
              module.files?.map(file => ({
                ...file,
                locked_linked_items:
                  typeof file.locked_linked_items === 'string'
                    ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
                    : file.locked_linked_items || [], // Keep array or default to empty
                additionalFiles:
                  typeof file.additionalFiles === 'string'
                    ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
                    : file.additionalFiles || [] // Keep array or default to empty
              })) || []
          }));
        };
        const syncExpandedStates = (
          transformed: ModuleData[],
          existing: ModuleData[]
        ) => {
          return transformed.map(module => {
            const matchingModule = existing.find(e => e.id === module.id);

            if (!matchingModule) return module;

            // Sync the `expanded` flag
            const updatedModule = {
              ...module,
              expanded: matchingModule.expanded || false,
              files:
                module?.files &&
                module?.files.map(file => {
                  const matchingFile =
                    matchingModule?.files &&
                    matchingModule.files.find(f => f.id === file.id);
                  return {
                    ...file,
                    fileexpanded: matchingFile?.fileexpanded || false
                  };
                })
            };

            return updatedModule;
          });
        };

        // Handling API result
        if (result.data[1]) {
          const transformData = await processModulesData(result.data[1]);
          const updatedTransformed = syncExpandedStates(
            transformData,
            ModulePreviewData
          );
          await SetModulePreviewData(updatedTransformed);
        }
        setIsLoaded(false);
        setSaved(true);
        setTabsActive(false);
        setActiveTab(1);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
    setTabsActive(false);
  };

  return (
    <FormProvider {...methods}>
      <Drawer
        open={openDetails}
        anchor="right"
        slotProps={{
          backdrop: {invisible: true}
        }}
        PaperProps={{
          sx: {
            width: {
              xs: 1,
              sm: '47vw'
            }
          }
        }}>
        <DialogToolBar
          title={task ? 'Edit File' : 'Add File'}
          onCloseDetails={onCloseDetails}
        />
        <Divider />
        <Box sx={{width: '100%'}}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}>
            <Grid item xs={12}>
              <Tabs
                value={activeTab}
                onChange={handleChange}
                variant="fullWidth"
                textColor="primary"
                sx={{
                  backgroundColor: '#F4F6F8',
                  '& .MuiTab-root': {
                    fontSize: '16px',
                    fontWeight: '500',
                    '&:hover': {
                      backgroundColor: '#e0e0e0', // Change background on hover
                      borderRadius: 2
                    }
                  },
                  '& .MuiTab-root.Mui-selected': {
                    color: 'primary.main',
                    margin: 1,
                    backgroundColor: 'white',
                    borderRadius: 2
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#637381',
                    height: 0
                  }
                }}>
                <Tab label="Basic Info" />
                <Tab label="Lock Properties" disabled={isloading || isloaded} />
                <Tab label="Resources" disabled={isloading || isloaded} />
              </Tabs>
            </Grid>
          </Grid>
        </Box>
        {activeTab === 0 &&
          (isloading ? (
            <Stack
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              minHeight={'50vh'}>
              <LoadingScreen />
            </Stack>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} style={{overflowY: 'auto'}}>
              <Stack spacing={3} sx={{pt: 3, pb: 5, px: 2.5}}>
                {/* Task Name */}
                <Stack direction="row">
                  <StyledLabel>Name</StyledLabel>
                  <Controller
                    name="taskName"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        error={!!errors.taskName}
                        helperText={errors.taskName?.message}
                      />
                    )}
                  />
                </Stack>

                <Stack direction="row">
                  <StyledLabel>Duration (minutes)</StyledLabel>
                  <Controller
                    name="duration"
                    control={control}
                    defaultValue={null} // Initial value is null
                    rules={{
                      validate: value => value !== 0 || 'Duration cannot be 0' // Custom validation
                    }}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        type="number"
                        error={!!errors.duration}
                        helperText={errors.duration?.message}
                        onChange={event => {
                          const newValue = event.target.value;
                          if (newValue === '' || parseInt(newValue, 10) > 0) {
                            field.onChange(
                              newValue === '' ? null : parseInt(newValue, 10)
                            );
                          }
                        }}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                </Stack>

                <Stack direction="row">
                  <StyledLabel>Description</StyledLabel>
                  <Controller
                    name="taskDescription"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        size="small"
                        sx={{minHeight: '11vh'}}
                        error={!!errors.taskDescription}
                        helperText={errors.taskDescription?.message}
                      />
                    )}
                  />
                </Stack>

                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">Attachments</Typography>
                  <RHFSingleUpload
                    thumbnail
                    name="uploadResourcesfiles"
                    onDrop={handleDrop}
                    onRemove={handleRemoveFile}
                    onRemoveAll={handleRemoveAllFiles}
                    onUpload={() => console.info('ON UPLOAD')}
                  />
                </Stack>
                {/* <Stack spacing={1} direction="row">
                  <RHFCheckbox
                    name="MarkFileAsMandatory"
                    label="Mark File As Mandatory"
                  />
                </Stack> */}
                {/* <Stack spacing={1} direction="row">
                  <RHFCheckbox
                    name="MarkAsIntroductoryFile"
                    label="Mark As Introductory File"
                  />
                </Stack> */}
                {/* <Stack spacing={1} direction="row">
                  <RHFCheckbox name="DisableSeek" label="Disable Seek" />
                </Stack> */}
                <Stack spacing={1} direction="row">
                  <RHFCheckbox
                    name="EnableFileDownload"
                    label="Enable File Download"
                  />
                </Stack>

                <Stack display="flex" flexDirection="row-reverse">
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="info"
                    disabled={isSubmitting}
                    loading={isloaded}
                    onClick={() => {
                      if (!errors) {
                        onSubmit;
                      }
                    }}>
                    Save
                  </LoadingButton>
                </Stack>
              </Stack>
            </form>
          ))}
        {activeTab === 1 && (
          <NewFilesLockProperties
            uiFields={uiFields}
            Moduledata={task?.data ? task.data : undefined}
            insertdata={insertedId}
            tableNameEntity="ModuleFileSubList"
          />
        )}
        {activeTab === 2 && (
          <Resources
            uiFields={uiFields}
            Moduledata={task?.data ? task.data : undefined}
            insertdata={insertedId}
            tableNameEntity="ContentFiles"
          />
        )}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert
            onClose={handleSnackbarClose}
            severity="warning"
            sx={{width: '40vw'}}>
            Basic information data must be added before accessing other tabs.
          </Alert>
        </Snackbar>
        <Snackbar
          open={saved}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert
            onClose={handleSnackbarClose}
            severity="success"
            sx={{width: '40vw'}}>
            Basic Info is Saved
          </Alert>
        </Snackbar>
      </Drawer>
    </FormProvider>
  );
}
